import {
    Avatar,
    Box,
    Button,
    Chip,
    Grid,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import GridMediaCard from "../../components/cards/GridMediaCard";
import BannerCarousel from "../../components/BannerCarousel";
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Fetch from "../../services/Fetch";
import AuthContext from "../../context/AuthContext";
import { useConstants } from "../../hooks/UseConstants";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import { formatCurrency } from "../../helper/formatCurrency";
import LogoImage from "../../images/logo/logo.png";
import { APP_NAME } from "../../constants/brand";
import HeroIllustration from "../../images/login/image1.png";

function parseList(result) {
    if (result?.status !== 200) return [];
    const payload = result.data?.data;
    return Array.isArray(payload) ? payload : (payload?.data || []);
}

function Home() {
    const theme = useTheme();
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [banners, setBanners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [courses, setCourses] = useState([]);
    const [enrolledCount, setEnrolledCount] = useState(0);
    const [walletBalance, setWalletBalance] = useState(Number(user?.wallet?.balance ?? 0));

    const teachers = useMemo(() => {
        const map = new Map();
        courses.forEach((c) => {
            if (c.teacher?.id) map.set(c.teacher.id, c.teacher);
        });
        return Array.from(map.values());
    }, [courses]);

    const displayName = user?.first_name || (isAr ? 'طالب' : 'Student');
    const majorName = isAr ? user?.major?.name_ar : user?.major?.name_en;

    const openBanner = (banner) => {
        if (banner.category === 'course' && banner.category_id) {
            navigate(`/courses/${banner.category_id}`);
        } else if (banner.category === 'path' && banner.category_id) {
            navigate(`/paths/${banner.category_id}`);
        }
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            const q = user?.major?.id ? '&for_user=1' : '';
            const [b, c, cr] = await Promise.all([
                Fetch(host + '/banners?per_page=8', 'GET'),
                Fetch(host + `/categories?per_page=12${q}`, 'GET'),
                Fetch(host + `/courses?per_page=20&status[]=accepted${q}`, 'GET'),
            ]);
            setBanners(parseList(b));
            setCategories(parseList(c));
            setCourses(parseList(cr));

            if (user) {
                const [profile, my] = await Promise.all([
                    Fetch(host + '/account/get-profile', 'GET'),
                    Fetch(host + '/student/buy/', 'GET'),
                ]);
                if (profile.status === 200) {
                    setWalletBalance(Number(profile.data?.data?.wallet?.balance ?? 0));
                } else {
                    setWalletBalance(Number(user?.wallet?.balance ?? 0));
                }
                if (my.status === 200) {
                    const enrolled = my.data?.data;
                    setEnrolledCount(Array.isArray(enrolled) ? enrolled.length : 0);
                }
            } else {
                setWalletBalance(0);
                setEnrolledCount(0);
            }
            setLoading(false);
        };
        load();
    }, [host, user?.major?.id, user?.id]);

    if (loading) {
        return (
            <Box>
                <Skeleton variant="rounded" height={320} sx={{ mb: 3, borderRadius: 4 }} />
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Grid key={i} size={{ xs: 6, md: 3 }}>
                            <Skeleton variant="rounded" height={100} sx={{ borderRadius: 3 }} />
                        </Grid>
                    ))}
                </Grid>
                <Skeleton variant="rounded" height={140} sx={{ mb: 2, borderRadius: 3 }} />
                <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                        <Grid key={i} size={{ xs: 6, sm: 4, md: 3 }}>
                            <Skeleton variant="rounded" height={200} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 2 }}>
            {/* Hero — logo, intro, welcome image */}
            <Box
                className="home-hero-banner"
                sx={{
                    position: 'relative',
                    borderRadius: 4,
                    overflow: 'hidden',
                    mb: 3,
                    minHeight: { xs: 360, md: 340 },
                    color: '#fff',
                    boxShadow: '0 20px 50px rgba(13, 53, 140, 0.18)',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, #0D358C 0%, #1a5bb8 42%, #457b9d 78%, #040E26 100%)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        width: 280,
                        height: 280,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.06)',
                        top: -90,
                        insetInlineEnd: -70,
                        animation: 'homeFloat 7s ease-in-out infinite',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: isAr
                            ? 'linear-gradient(105deg, rgba(4, 14, 38, 0.55) 0%, rgba(13, 53, 140, 0.35) 45%, transparent 72%)'
                            : 'linear-gradient(255deg, rgba(4, 14, 38, 0.55) 0%, rgba(13, 53, 140, 0.35) 45%, transparent 72%)',
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 3,
                        flexDirection: { xs: 'column', md: isAr ? 'row' : 'row-reverse' },
                        p: { xs: 3.5, md: 5 },
                        minHeight: { xs: 360, md: 340 },
                    }}
                >
                    <Box sx={{ flex: 1, maxWidth: { md: '58%' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box className="hero-logo-card">
                                <Box
                                    component="img"
                                    src={LogoImage}
                                    alt={APP_NAME}
                                />
                            </Box>
                            <Box>
                                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.2, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                                    {APP_NAME}
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.88, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>
                                    {isAr ? 'منصة التعلّم الإلكتروني' : 'E-Learning Platform'}
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body1" sx={{ opacity: 0.92, mb: 2, lineHeight: 1.85, maxWidth: 560, fontSize: { xs: '1rem', md: '1.125rem' } }}>
                            {isAr
                                ? 'منصتك التعليمية الذكية — تعلّم من أفضل المدرسين، تابع تقدمك خطوة بخطوة، واحصل على شهادات معترف بها في أي وقت ومن أي مكان.'
                                : 'Your smart learning hub — learn from top instructors, track your progress, and earn recognized certificates anytime, anywhere.'}
                        </Typography>

                        <Chip
                            icon={<TrendingUpIcon sx={{ color: '#fff !important' }} />}
                            label={isAr ? `مرحباً، ${displayName} 👋` : `Welcome, ${displayName} 👋`}
                            size="medium"
                            sx={{
                                mb: 1.5,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: { xs: '0.95rem', md: '1.05rem' },
                                py: 2,
                                backdropFilter: 'blur(8px)',
                            }}
                        />
                        {majorName && (
                            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 600 }}>
                                {isAr ? `تخصصك: ${majorName}` : `Your major: ${majorName}`}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            <Button
                                variant="contained"
                                startIcon={<PlayCircleOutlineIcon />}
                                onClick={() => navigate('/courses')}
                                sx={{
                                    bgcolor: '#fff',
                                    color: '#0D358C',
                                    fontWeight: 700,
                                    px: 3,
                                    '&:hover': { bgcolor: '#f0f4ff' },
                                }}
                            >
                                {isAr ? 'استكشف الدورات' : 'Explore courses'}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/my-courses')}
                                sx={{
                                    borderColor: 'rgba(255,255,255,0.6)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                {isAr ? 'دوراتي' : 'My courses'}
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: '0 0 auto',
                            width: 280,
                        }}
                    >
                        <Box
                            component="img"
                            src={HeroIllustration}
                            alt=""
                            className="home-hero-illustration"
                            sx={{
                                width: '100%',
                                maxHeight: 240,
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.25))',
                                mixBlendMode: 'lighten',
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Admin banners carousel */}
            {banners.length > 0 && (
                <ScrollReveal delay={40}>
                    <BannerCarousel banners={banners} isAr={isAr} onBannerClick={openBanner} />
                </ScrollReveal>
            )}

            {/* Stats — same style as admin dashboard */}
            <ScrollReveal>
            <Box className="flex justify-between flex-wrap mb-4 max-sm:block" dir={isAr ? 'rtl' : 'ltr'}>
                <DashboardStatCard
                    label={isAr ? 'دوراتي' : 'My courses'}
                    value={enrolledCount}
                    icon={<SchoolOutlinedIcon />}
                    accent="blue"
                    onClick={() => navigate('/my-courses')}
                    className="w-full min-w-[140px] flex-[1_1_22%] mx-2 max-sm:mx-auto"
                />
                <DashboardStatCard
                    label={isAr ? 'دورات مقترحة' : 'Suggested'}
                    value={courses.length}
                    icon={<MenuBookOutlinedIcon />}
                    accent="sky"
                    onClick={() => navigate('/courses')}
                    className="w-full min-w-[140px] flex-[1_1_22%] mx-2 max-sm:mx-auto max-sm:mt-2"
                />
                <DashboardStatCard
                    label={isAr ? 'رصيد المحفظة' : 'Wallet'}
                    value={formatCurrency(walletBalance, isAr)}
                    icon={<AccountBalanceWalletOutlinedIcon />}
                    accent="teal"
                    onClick={() => navigate('/wallet')}
                    className="w-full min-w-[140px] flex-[1_1_22%] mx-2 max-sm:mx-auto max-sm:mt-2"
                />
                <DashboardStatCard
                    label={isAr ? 'التصنيفات' : 'Categories'}
                    value={categories.length}
                    icon={<WorkspacePremiumOutlinedIcon />}
                    accent="indigo"
                    onClick={() => navigate('/courses')}
                    className="w-full min-w-[140px] flex-[1_1_22%] mx-2 max-sm:mx-auto max-sm:mt-2"
                />
            </Box>
            </ScrollReveal>

            {/* Teachers */}
            {teachers.length > 0 && (
                <ScrollReveal delay={100}>
                <DashboardPanelCard
                    title={isAr ? 'مدرسون مميزون' : 'Featured instructors'}
                    className="mb-4 mx-0"
                >
                    <Box className="home-banner-track" sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                        {teachers.map((t, i) => {
                            const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
                            return (
                                <ScrollReveal key={t.id} delay={i * 60} direction="up">
                                <GridMediaCard
                                    className="min-w-[130px] max-w-[150px] text-center"
                                    title={name}
                                    onClick={() => navigate(`/teachers/${t.id}`)}
                                    header={
                                        <Avatar src={t.image} sx={{ width: 56, height: 56, mx: 'auto' }}>
                                            {name[0]}
                                        </Avatar>
                                    }
                                />
                                </ScrollReveal>
                            );
                        })}
                    </Box>
                </DashboardPanelCard>
                </ScrollReveal>
            )}

            {/* Categories */}
            {categories.length > 0 && (
                <ScrollReveal delay={120}>
                <DashboardPanelCard title={isAr ? 'تصفح حسب التصنيف' : 'Browse by category'} className="mb-4 mx-0">
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {categories.map((cat) => (
                            <Chip
                                key={cat.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span>{isAr ? cat.name_ar || cat.name_en : cat.name_en || cat.name_ar}</span>
                                        {cat.courses_count != null && (
                                            <Typography component="span" variant="caption" sx={{ opacity: 0.7 }}>
                                                ({cat.courses_count})
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                onClick={() => navigate(`/courses?category_id=${cat.id}`)}
                                sx={{
                                    py: 2.5,
                                    px: 1.5,
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                    borderRadius: '14px',
                                    bgcolor: '#f8fafc',
                                    border: '1px solid #dbeafe',
                                    color: '#0D358C',
                                    transition: 'all 0.25s ease',
                                    '&:hover': {
                                        bgcolor: '#0D358C',
                                        color: '#fff',
                                        borderColor: '#0D358C',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 20px rgba(13,53,140,0.18)',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </DashboardPanelCard>
                </ScrollReveal>
            )}

            <ScrollReveal delay={140}>
            <DashboardPanelCard title={isAr ? 'دورات مقترحة لك' : 'Recommended for you'} className="mx-0">
                {courses.length === 0 ? (
                    <Typography color="text.secondary" className="text-center py-8">
                        {isAr ? 'لا توجد دورات حالياً' : 'No courses available yet'}
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {courses.slice(0, 8).map((course, i) => (
                            <Grid key={course.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                <ScrollReveal delay={i * 50} direction="up">
                                <GridMediaCard
                                    image={resolveCourseImage(course)}
                                    title={isAr ? course.name_ar || course.name_en : course.name_en || course.name_ar}
                                    description={
                                        course.teacher
                                            ? `${course.teacher.first_name} ${course.teacher.last_name}`
                                            : undefined
                                    }
                                    onClick={() => navigate(`/courses/${course.id}`)}
                                    footer={
                                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#0D358C' }}>
                                            {course.is_free || Number(course.price) === 0
                                                ? (isAr ? 'مجاني' : 'Free')
                                                : formatCurrency(course.effective_price ?? course.price, isAr)}
                                        </Typography>
                                    }
                                />
                                </ScrollReveal>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DashboardPanelCard>
            </ScrollReveal>
        </Box>
    );
}

export default Home;
